import {
  ArticleType,
  AutoAssetDetails,
  AutomobileArticleDetails,
  BodyType,
  Category,
  FuelType,
  Gear,
} from '@nemuru-eng/js-api-client';

import { AutomobileDataEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileDataEntity';
import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { normalizeSpanishLicensePlate } from '@/utils/zodValidation/validateSpanishLicensePlate';

export class AutomobileItem extends Item<AutomobileFormItem> {
  constructor(data: AutomobileFormItem, totalDiscount?: number) {
    super(data);
    this.totalDiscount = totalDiscount || 0;
  }

  protected isCompleted(): boolean {
    return !!this.data.brand && !!this.data.model && !!this.data.version && this.isCompletedVehicleAge();
  }

  private isCompletedVehicleAge(): boolean {
    return this.data.isNewVehicle || !!this.data.registrationDate;
  }

  protected toForm(): AutomobileFormItem {
    return {
      ...this.toMinimalFormItem(),
      ...this.data,
    } as AutomobileFormItem;
  }

  public updateDiscount(discount: number): AutomobileItem {
    this.totalDiscount = discount;
    return this;
  }

  protected toDto() {
    return {
      description: this.data.description,
      brand: this.data.brand,
      model: this.data.model,
      version: this.data.version,
      ...(!!this.data.brandCode && {
        brandCode: this.data.brandCode,
      }),
      ...(!!this.data.modelCode && {
        modelCode: this.data.modelCode,
      }),
      ...(!!this.data.versionCode && {
        versionCode: this.data.versionCode,
      }),
      isNewVehicle: this.data.isNewVehicle,
      ...(!!this.data.plateNumber && {
        usage: {
          plateNumber: normalizeSpanishLicensePlate(this.data.plateNumber),
          ...(!!this.data.odometer && {
            odometer: parseInt(this.data.odometer),
          }),
        },
      }),
      category: this.data.category,
      autoCategory: this.data.autoCategory,
      specs: {
        bodyType: this.data.bodyType,
        power: this.data.power,
        engine: this.data.engine,
        gear: this.data.gear,
        ...(!!this.data.identificationNumber && {
          identificationNumber: this.data.identificationNumber,
        }),
        ...(!!this.data.registrationDate && {
          registrationDate: this.data.registrationDate,
        }),
        fuelType: FuelType.GASOLINE, // FIXME mock
        imported: false, // FIXME mock
      } as Partial<AutoAssetDetails['specs']>,
    };
  }

  public static fromOrderLineDto(
    units: number,
    article: AutomobileArticleDetails,
    totalDiscount: number,
  ): AutomobileItem {
    const formData: AutomobileFormItem = {
      // Minimal form item fields
      units: units.toString(),
      vat: '21', // FIXME: Default VAT
      price: article.price.amount.toFixed(2).toString(),
      category: article.category,

      // ArticleType-specific fields
      type: ArticleType.AUTOMOBILE,
      description: article.name || article.description || '',
      brand: article.brand || '',
      model: article.model || '',
      version: article.version || '',
      identificationNumber: article.specs?.identificationNumber || '',
      isNewVehicle: article.isNew ?? !article.specs?.registrationDate,
      registrationDate: article.specs?.registrationDate,
      plateNumber: article.usage?.plateNumber ? normalizeSpanishLicensePlate(article.usage.plateNumber) : '',
      odometer: article.usage?.odometer?.toString() || null,

      // Additional automobiledata from CDN
      brandCode: article.brandCode ?? null,
      modelCode: article.modelCode ?? null,
      versionCode: article.versionCode ?? null,
      autoCategory: article.autoCategory || Category.CAR,
      bodyType: article.specs?.bodyType as BodyType,
      power: article.specs?.power || 0,
      engine: article.specs?.engine || 0,
      gear: article.specs?.gear as Gear,
    };

    return new AutomobileItem(formData, totalDiscount);
  }

  public get model(): string {
    return this.data.model;
  }

  public get registrationDate(): Date | undefined {
    if (!this.data.registrationDate) return undefined;
    return new Date(this.data.registrationDate);
  }

  static async isInCdn(item: AutomobileItem, automobileData: AutomobileDataEntity): Promise<boolean> {
    const { brand, model, version } = item.data;

    if (!brand || !model || !version || !automobileData) {
      return false;
    }

    // Find brand in CDN
    if (!automobileData?.brands.find((option) => option.value === brand)) {
      return false;
    }

    // Find model in CDN
    await automobileData.updateBrand(brand);
    if (!automobileData?.brandModels.find((option) => option.value === model)) {
      return false;
    }

    // Find version in CDN
    await automobileData.updateModel(model);
    if (!automobileData?.versions.find((option) => option.value === version)) {
      return false;
    }

    return true;
  }
}
